import { useMemo } from 'react'
import type { SafetyFunction } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const SAFETY_FUNCTION_NONE_VALUE = '__none__'

export type SafetyFunctionSelectProps = {
  value: string | null
  onChange: (value: string | null) => void
  safetyFunctions: SafetyFunction[]
  disabled?: boolean
  id?: string
}

function getSafetyFunctionOptionLabel(
  safetyFunction: SafetyFunction,
  translate: (key: string, options?: { referenceId: string }) => string
): string {
  const eplanReferenceId = safetyFunction.eplanReferenceId
  const hasEplanReferenceId =
    eplanReferenceId != null && eplanReferenceId.trim() !== ''

  if (hasEplanReferenceId) {
    return `${safetyFunction.name} (${translate('safetyFunctionsPage.card.eplanReferenceId', { referenceId: eplanReferenceId })})`
  }

  return safetyFunction.name
}

export function SafetyFunctionSelect({
  value,
  onChange,
  safetyFunctions,
  disabled = false,
  id,
}: SafetyFunctionSelectProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const hasSafetyFunctions = safetyFunctions.length > 0
  const hasSelectedValue = value != null && value.trim() !== ''
  const isDisabled = disabled || !hasSafetyFunctions

  let selectValue: string | undefined
  if (hasSelectedValue) {
    selectValue = value
  } else if (hasSafetyFunctions) {
    selectValue = SAFETY_FUNCTION_NONE_VALUE
  }

  const handleValueChange = (newValue: string) => {
    if (newValue === SAFETY_FUNCTION_NONE_VALUE) {
      onChange(null)
      return
    }

    onChange(newValue)
  }

  let placeholder = t(
    'riskReductionMeasuresLibraryPage.form.safetyFunctionPlaceholder'
  )
  if (!hasSafetyFunctions) {
    placeholder = t(
      'riskReductionMeasuresLibraryPage.form.safetyFunctionEmptyHint'
    )
  }

  const selectItems = useMemo(() => {
    const items: Array<{ value: string; label: string }> = []
    if (hasSafetyFunctions) {
      items.push({
        value: SAFETY_FUNCTION_NONE_VALUE,
        label: t('riskReductionMeasuresLibraryPage.form.safetyFunctionNone'),
      })
    }
    for (const safetyFunction of safetyFunctions) {
      items.push({
        value: safetyFunction.id,
        label: getSafetyFunctionOptionLabel(safetyFunction, t),
      })
    }
    return items
  }, [hasSafetyFunctions, safetyFunctions, t])

  return (
    <Select
      value={selectValue}
      onValueChange={(value) => {
        if (value) {
          handleValueChange(value)
        }
      }}
      disabled={isDisabled}
      items={selectItems}
    >
      <SelectTrigger id={id} className='w-full'>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {hasSafetyFunctions ? (
          <SelectItem value={SAFETY_FUNCTION_NONE_VALUE}>
            {t('riskReductionMeasuresLibraryPage.form.safetyFunctionNone')}
          </SelectItem>
        ) : null}
        {safetyFunctions.map((safetyFunction) => (
          <SelectItem key={safetyFunction.id} value={safetyFunction.id}>
            {getSafetyFunctionOptionLabel(safetyFunction, t)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
